'use client';

// Client password-reset REQUEST island (M9). Anonymous: posts an email to the
// same-origin BFF (/api/auth/password-reset/request), which proxies Fastify and
// forwards the tenant via x-tenant-domaine. The endpoint is anti-enumeration —
// it always answers 200 { ok: true } for a well-formed body — so on success the
// form shows a GENERIC confirmation that never reveals whether the address maps
// to an account. No token or email is persisted in client state.

import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { interpretRequest } from '@/lib/passwordReset/flow';

type Phase = 'form' | 'sent' | 'error';

export function RequestForm() {
  const [email, setEmail] = useState('');
  const [phase, setPhase] = useState<Phase>('form');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canSubmit = email.trim().length > 0 && !isSubmitting;

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canSubmit) return;
    setIsSubmitting(true);

    try {
      const res = await fetch('/api/auth/password-reset/request', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const outcome = interpretRequest(res.status);
      switch (outcome.kind) {
        case 'sent':
          setPhase('sent');
          return;
        case 'rateLimited':
          toast.error(outcome.message);
          return;
        case 'error':
          setPhase('error');
          return;
      }
    } catch {
      setPhase('error');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (phase === 'sent') {
    return (
      <div role="status" className="space-y-3">
        <p className="text-foreground text-sm">
          Si un compte existe pour cette adresse, un email contenant un lien de
          réinitialisation vient d&apos;être envoyé. Pensez à vérifier vos
          courriers indésirables.
        </p>
        <p className="text-muted-foreground text-xs">
          Si vous n&apos;avez jamais activé votre compte, ce lien ne fonctionnera
          pas : utilisez plutôt le lien d&apos;activation reçu lors de la création
          de votre compte.
        </p>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div role="alert" className="space-y-2">
        <h2 className="text-foreground text-lg font-semibold">
          Une erreur est survenue
        </h2>
        <p className="text-muted-foreground text-sm">
          Le service est momentanément indisponible. Réessayez dans un instant.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" noValidate>
      <div className="space-y-1.5">
        <Label htmlFor="email">Adresse email</Label>
        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isSubmitting}
        />
      </div>

      <Button type="submit" size="lg" disabled={!canSubmit} className="w-full">
        {isSubmitting ? 'Envoi…' : 'Envoyer le lien de réinitialisation'}
      </Button>
    </form>
  );
}
